import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { IImpact } from "@/pages/dashboard/utils/types";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export type DatasetType =
  | "custom"
  | "epd"
  | "root"
  | "eco_invent"
  | "agri_footprint"
  | "eol"
  | "exiobase"
  | "packaging";

export interface ImpactDataset {
  id: number;
  confidence?: number;
  name: string;
  geography: string;
  unit: string;
  dataset_type: DatasetType;
  impact: IImpact;
}

export interface EcoInventDataset extends ImpactDataset {
  activity_name: string;
  reference_product_name: string;
  sector: string;
  special_activity_type: string;
}

export const datasetTypeConfig: Record<DatasetType, { key: DatasetType; label: string }> = {
  eco_invent: {
    key: "eco_invent",
    label: "EcoInvent",
  },
  agri_footprint: {
    key: "agri_footprint",
    label: "Agri-footprint",
  },
  exiobase: {
    key: "exiobase",
    label: "Exiobase",
  },
  packaging: {
    key: "packaging",
    label: "Packaging",
  },
  custom: {
    key: "custom",
    label: "Custom",
  },
  epd: {
    key: "epd",
    label: "EPD",
  },
  root: {
    key: "root",
    label: "Root",
  },
  eol: {
    key: "eol",
    label: "End of life",
  },
};

export function datasetTypeToLabel(datasetType: DatasetType) {
  return datasetTypeConfig[datasetType].label;
}

export interface GetImpactDatasets {
  total_count: number;
  datasets: ImpactDataset[];
}

export interface GetEcoInventDatasets {
  total_count: number;
  datasets: EcoInventDataset[];
}

export interface GetEcoInventSuggestions {
  suggestions: ImpactDataset[];
}

export function useImpactDatasets({
  pagination,
  filter,
  search,
  sort,
  enabled,
  includeExiobase = false,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  includeExiobase?: boolean;
}) {
  return useQuery({
    queryKey: ["datasets", pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetImpactDatasets>("/impact_references/datasets", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            include_exiobase: includeExiobase,
          },
        })
        .then((result) => result.data),
  });
}

export function useEcoInventDatasets({
  pagination,
  filter,
  search,
  sort,
  enabled,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["datasets", pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetEcoInventDatasets>("/impact_references/eco_invent_datasets", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function useGetEcoInventSuggestions({
  enabled,
  materialId,
}: {
  enabled?: boolean;
  materialId: number;
}) {
  return useQuery({
    queryKey: ["datasets", "ecoinvent", "suggestions", materialId],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetEcoInventSuggestions>("/impact_references/suggestions", {
          params: {
            material_id: materialId,
          },
        })
        .then((result) => result.data),
  });
}
